import React, { useLayoutEffect, useRef } from 'react';
import { NODE_WIDTH, NODE_HEIGHT } from '../../utils/edgePaths';

function coord(value) {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Move the pulled line onto the cursor without waiting for React to redraw. */
export function paintConnectionLine(path, tip, startX, startY, endX, endY) {
  if (!path) return;
  if (!Number.isFinite(startX) || !Number.isFinite(startY) || !Number.isFinite(endX) || !Number.isFinite(endY)) return;
  path.setAttribute('d', `M ${startX} ${startY} L ${endX} ${endY}`);
  if (!tip) return;
  const x = endX + 16;
  const y = endY - 8;
  const rect = tip.firstElementChild;
  const text = tip.lastElementChild;
  if (rect) {
    rect.setAttribute('x', x);
    rect.setAttribute('y', y - 12);
  }
  if (text) {
    text.setAttribute('x', x + 10);
    text.setAttribute('y', y + 4);
  }
}

/**
 * The line you pull while connecting missions.
 * It stays a straight line from the handle to the cursor the whole way,
 * including while the pointer is over another mission.
 * On the board, the pointer handler paints the line itself so it stays on the cursor.
 */
const ConnectionPreview = ({
  connectingFrom,
  connectingTarget,
  connectingBranch,
  connectingStartPos,
  nodePositions,
  nodeDimensions,
  mousePos,
  tasks = [],
  lineRef = null,
  endRef = null,
}) => {
  const positions = nodePositions || {};
  const dimensions = nodeDimensions || {};
  const from = positions[connectingFrom];
  const fromDims = dimensions[connectingFrom] || { width: NODE_WIDTH, height: NODE_HEIGHT };
  const startX = connectingStartPos
    ? coord(connectingStartPos.x)
    : coord(from?.x) == null ? null : coord(from.x) + (coord(fromDims.width) ?? NODE_WIDTH) / 2;
  const startY = connectingStartPos
    ? coord(connectingStartPos.y)
    : coord(from?.y) == null ? null : coord(from.y) + (coord(fromDims.height) ?? NODE_HEIGHT) / 2;

  const pathRef = useRef(null);
  const tipRef = useRef(null);
  const live = endRef?.current;
  const endX = live && Number.isFinite(live.x) ? live.x : mousePos?.x;
  const endY = live && Number.isFinite(live.y) ? live.y : mousePos?.y;

  useLayoutEffect(() => {
    if (!lineRef) return undefined;
    lineRef.current = { path: pathRef.current, tip: tipRef.current, startX, startY };
    const end = endRef?.current;
    if (end) paintConnectionLine(pathRef.current, tipRef.current, startX, startY, end.x, end.y);
    return () => {
      if (lineRef.current?.path === pathRef.current) lineRef.current = null;
    };
  }, [lineRef, endRef, startX, startY]);

  if ((!from && !connectingStartPos) || startX == null || startY == null) return null;
  if (!Number.isFinite(endX) || !Number.isFinite(endY)) return null;

  const pathD = `M ${startX} ${startY} L ${endX} ${endY}`;

  const sourceTask = tasks.find(t => t.id === connectingFrom);
  const isDecisionNode = sourceTask?.nodeType === 'decision';

  let branchColor = null;
  if (isDecisionNode && connectingBranch) {
    const options = sourceTask.options || [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
    ];
    const option = options.find(opt => opt.id === connectingBranch);
    branchColor = option?.color;
  }

  const strokeColor = branchColor
    || (connectingTarget ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.4)');
  const tooltipOffsetX = 16;
  const tooltipOffsetY = -8;

  return (
    <>
      <path
        ref={pathRef}
        d={pathD}
        fill="none"
        stroke={typeof strokeColor === 'string' ? strokeColor : 'rgba(255, 255, 255, 0.4)'}
        strokeWidth={1.5}
        strokeDasharray="6 4"
        className="flowchart__edge-preview"
      />

      <g
        ref={tipRef}
        className="flowchart__connection-tooltip"
        style={{ visibility: connectingTarget ? 'hidden' : 'visible' }}
      >
        <rect
          x={endX + tooltipOffsetX}
          y={endY + tooltipOffsetY - 12}
          width={180}
          height={24}
          rx={4}
          fill="rgba(30, 30, 50, 0.95)"
          stroke="rgba(255, 255, 255, 0.15)"
          strokeWidth={1}
        />
        <text
          x={endX + tooltipOffsetX + 10}
          y={endY + tooltipOffsetY + 4}
          fill="rgba(255, 255, 255, 0.8)"
          fontSize={11}
          fontFamily="inherit"
        >
          Drop on a dependent mission
        </text>
      </g>
    </>
  );
};

export default ConnectionPreview;
